// All API requests should be routed through this file

import { SubscriptionStatus } from "./types/PlanTypes";
import { SupportDashboardResponse } from "./types/SupportTypes";

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.execdash.ai/api'
  : 'http://localhost:5000/api';

// Helper function to include authentication token in requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Authentication API functions
export async function login(email: string, password: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const errorData = await response.json();

      throw new Error(errorData.message || 'Login failed');
    }
    return response.json();
  } catch (error) {
    throw new Error('There was an error logging in. Please try again later.');
  }
}

export async function register(userData: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
}) {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Registration failed');
  }
  return response.json();
}

// OAuth registration
export async function registerOAuth(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  oauthProvider: string;
  oauthProviderUserId: string;
  organizationName?: string;
}) {
  const response = await fetch(`${API_BASE_URL}/auth/register/oauth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'OAuth registration failed');
  }
  return response.json();
}

// OAuth login
export async function loginOAuth(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  oauthProvider: string;
  oauthProviderUserId: string;
}) {
  const response = await fetch(`${API_BASE_URL}/auth/login/oauth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'OAuth login failed');
  }
  return response.json();
}

export async function getUserProfile() {
  const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      throw new Error('Authentication required');
    }
    throw new Error('Failed to get user profile');
  }
  return response.json();
}

export async function getAllUsers() {
  const response = await fetch(`${API_BASE_URL}/auth/users`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }
  return response.json();
}

export async function getUsersByCustomerId(customerId: number) {
  const response = await fetch(`${API_BASE_URL}/auth/users/customer/${customerId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch customer users');
  }
  return response.json();
}

export async function createUser(userData: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  customerId: number;
  role: string;
}) {
  const response = await fetch(`${API_BASE_URL}/auth/users`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create user');
  }
  return response.json();
}

export async function updateUser(userId: number, userData: {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  isActive?: boolean;
}) {
  const response = await fetch(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update user');
  }
  return response.json();
}

export async function deleteUser(userId: number) {
  const response = await fetch(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete user');
  }
  return response.json();
}



// Password reset functions
export async function forgotPassword(email: string) {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to send reset code');
  }
  return response.json();
}

export async function verifyResetCode(email: string, resetCode: string) {
  const response = await fetch(`${API_BASE_URL}/auth/verify-reset-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, resetCode }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Invalid reset code');
  }
  return response.json();
}

export async function resetPasswordWithCode(email: string, resetCode: string, newPassword: string) {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password-with-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, resetCode, newPassword }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Password reset failed');
  }
  return response.json();
}

// Admin password reset (testing only)
export async function resetPassword(userId: number, newPassword: string) {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, newPassword }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Password reset failed');
  }
  return response.json();
}

export async function getCustomers() {
  const response = await fetch(`${API_BASE_URL}/customers`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch customers');
  }
  return response.json();
}

export async function getCustomerById(customerId: number) {
  const response = await fetch(`${API_BASE_URL}/customers/${customerId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch customer details');
  }
  return response.json();
}

export async function getPlans() {
  const response = await fetch(`${API_BASE_URL}/plans`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch plans');
  }
  return response.json();
}

// Stripe API functions
export async function createCheckoutSession(planId: number) {
  const response = await fetch(`${API_BASE_URL}/stripe/create-checkout-session`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ planId }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create checkout session');
  }
  return response.json();
}

export async function previewCancellation(): Promise<{ currentPeriodEnd: string; formattedDate: string }> {
  const response = await fetch(`${API_BASE_URL}/stripe/preview-cancellation`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to preview cancellation');
  }
  const result = await response.json();
  return result.data;
}

export async function cancelSubscription(immediate: boolean = false, previewCancellationDate: string = '') {
  const response = await fetch(`${API_BASE_URL}/stripe/cancel-subscription`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      immediate,
      previewCancellationDate: previewCancellationDate ? new Date(previewCancellationDate) : null
    }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to cancel subscription');
  }
  return response.json();
}

export async function previewUpgrade(planId: number) {
  const response = await fetch(`${API_BASE_URL}/stripe/preview-upgrade`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ planId }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to preview upgrade cost');
  }
  return response.json();
}

export async function upgradeSubscription(planId: number) {
  const response = await fetch(`${API_BASE_URL}/stripe/upgrade-subscription`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ planId }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to upgrade subscription');
  }
  return response.json();
}

export async function scheduleDowngrade(planId: number) {
  const response = await fetch(`${API_BASE_URL}/stripe/schedule-downgrade`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ planId }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to schedule downgrade');
  }
  return response.json();
}

export async function cancelPendingChange() {
  const response = await fetch(`${API_BASE_URL}/stripe/cancel-pending-change`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to cancel pending change');
  }
  return response.json();
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
  const response = await fetch(`${API_BASE_URL}/plans/customer-plan-status`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    if (response.status === 404) {
      return null; // No subscription found
    }
    throw new Error('Failed to get customer plan status');
  }
  const dataObj = await response.json();
  if (dataObj.success) {
    return dataObj.data;
  }
  else {
    throw new Error(dataObj.message || 'Failed to get customer plan status');
  }
}

export async function createPortalSession() {
  const response = await fetch(`${API_BASE_URL}/stripe/create-portal-session`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create portal session');
  }
  return response.json();
}

export async function fetchHello(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/hello`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getOverview() {
  const response = await fetch(`${API_BASE_URL}/overview`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getMarketInsights(customerId?: string | number) {
  // If customerId is provided, get insights for that customer, otherwise get for current user's customer
  if (!customerId) {
    throw new Error('Customer ID is required');
  }

  const endpoint = `/marketdata-ai/${customerId}`;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
  });

  // Special handling for 404 when no insights are available
  if (response.status === 404) {
    const errorData = await response.json();
    if (errorData.error === 'No AI insights available') {
      // Return null to indicate no insights are available yet
      return null;
    }
    if (errorData.error === 'No market data found for this customer' || errorData.error === 'MISSING_MARKET_DATA') {
      const error = new Error(errorData.message || 'Missing required market data');
      (error as any).error = 'MISSING_MARKET_DATA';
      throw error;
    }
  }

  // Handle plan upgrade required error
  if (response.status === 403) {
    const errorData = await response.json();
    if (errorData.error === 'PLAN_UPGRADE_REQUIRED') {
      const error = new Error(errorData.message || 'Plan upgrade required');
      (error as any).type = 'PLAN_UPGRADE_REQUIRED';
      throw error;
    }
  }

  // Handle missing market data validation error
  if (response.status === 400) {
    const errorData = await response.json();
    if (errorData.error === 'MISSING_MARKET_DATA') {
      const error = new Error(errorData.message || 'Missing required market data');
      (error as any).error = 'MISSING_MARKET_DATA';
      throw error;
    }
  }

  // Handle other error cases
  if (!response.ok) throw new Error('Failed to fetch market insights');

  return response.json();
}

export async function generateMarketInsights(customerId: string | number) {
  if (!customerId) {
    throw new Error('Customer ID is required');
  }

  const endpoint = `/marketdata-ai/generate/${customerId}`;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  // Handle error responses
  if (!response.ok) {
    // If it's a 429, it means generation is already in progress
    if (response.status === 429) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Generation already in progress');
    }

    // Handle plan upgrade required error
    if (response.status === 403) {
      const errorData = await response.json();
      if (errorData.error === 'PLAN_UPGRADE_REQUIRED') {
        const error = new Error(errorData.message || 'Plan upgrade required');
        (error as any).type = 'PLAN_UPGRADE_REQUIRED';
        throw error;
      }
    }

    // Handle missing market data validation error
    if (response.status === 400) {
      const errorData = await response.json();
      if (errorData.error === 'MISSING_MARKET_DATA') {
        const error = new Error(errorData.message || 'Missing required market data');
        (error as any).error = 'MISSING_MARKET_DATA';
        throw error;
      }
    }

    throw new Error('Failed to generate market insights');
  }

  // Return the JSON response
  return response.json();
}

export async function getOverviewAiInsight() {
  const response = await fetch(`${API_BASE_URL}/overview/ai/insight`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function postOverviewAiQuestion(question: string) {
  const response = await fetch(`${API_BASE_URL}/overview/ai/question`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ question }),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

// Combined insight API function
export async function getCombinedOverviewAiData(generateIfMissing: boolean = false, refresh?: boolean) {
  // Fetch both AI insights and dashboard metrics in parallel
  const [aiResponse, dashboardResponse] = await Promise.all([
    fetch(`${API_BASE_URL}/overview/ai/combined-insight`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ generateIfMissing, refresh }),
    }),
    fetch(`${API_BASE_URL}/overview/dashboard`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })
  ]);

  if (!aiResponse.ok) {
    const errorData = await aiResponse.json().catch(() => ({ error: 'Failed to fetch combined insight' }));
    // Handle plan upgrade required error
    if (aiResponse.status === 403 && errorData.error === 'PLAN_UPGRADE_REQUIRED') {
      const error = new Error(errorData.message || 'Plan upgrade required');
      (error as any).type = 'PLAN_UPGRADE_REQUIRED';
      throw error;
    }
    throw new Error(errorData.error || 'Failed to fetch combined insight');
  }

  if (!dashboardResponse.ok) {
    const errorData = await dashboardResponse.json().catch(() => ({ error: 'Failed to fetch dashboard metrics' }));
    // Handle plan upgrade required error
    if (dashboardResponse.status === 403 && errorData.error === 'PLAN_UPGRADE_REQUIRED') {
      const error = new Error(errorData.message || 'Plan upgrade required');
      (error as any).type = 'PLAN_UPGRADE_REQUIRED';
      throw error;
    }
    throw new Error(errorData.error || 'Failed to fetch dashboard metrics');
  }

  const aiData = await aiResponse.json();
  const dashboardData = await dashboardResponse.json();

  // Combine both datasets into a single response
  return {
    ...aiData,
    dashboardMetrics: dashboardData
  };
}


// Shared data store for development data to avoid multiple API calls
let developmentDataCache: any = null;
let lastFetchTime: number = 0;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Function to get all development data from a single endpoint
export async function getAllDevelopmentData() {
  const currentTime = Date.now();

  // Use cached data if available and not expired
  if (developmentDataCache && (currentTime - lastFetchTime < CACHE_EXPIRY_MS)) {
    return developmentDataCache;
  }

  // Fetch fresh data
  const response = await fetch(`${API_BASE_URL}/development/dashboard`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('API error');

  const data = await response.json();
  if (data && data.projects && data.projects.length === 0) {
    //only cache if there is data
    developmentDataCache = data;
    lastFetchTime = currentTime;
  }
  else {
    invalidateDevelopmentDataCache();
  }

  return data;
}

// Legacy functions that now use the shared data
// export async function getDevelopmentOverview() {
//   return getAllDevelopmentData();
// }

// export async function getDevelopmentSprint(sprintId: string | number) {
//   const data = await getAllDevelopmentData();
//   // Filter the data to get the specific sprint
//   return data;
// }

// export async function getDevelopmentIterations() {
//   const data = await getAllDevelopmentData();
//   return data;
// }

// New dedicated API functions for teams and developers

// Teams cache
let teamsDataCache: any = null;
let teamsLastFetchTime: number = 0;

// Function to get team data from the dedicated endpoint
export async function getTeamsData() {
  const currentTime = Date.now();

  // Use cached data if available and not expired
  if (teamsDataCache && (currentTime - teamsLastFetchTime < CACHE_EXPIRY_MS)) {
    return teamsDataCache;
  }

  // Fetch fresh data
  const response = await fetch(`${API_BASE_URL}/development/teams`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('API error');

  const data = await response.json();
  teamsDataCache = data;
  teamsLastFetchTime = currentTime;

  return data;
}

// Developers cache
let developersDataCache: any = null;
let developersLastFetchTime: number = 0;

// Function to get developer data from the dashboard endpoint
// export async function getDevelopersData() {
//   const currentTime = Date.now();

//   // Use cached data if available and not expired
//   if (developersDataCache && (currentTime - developersLastFetchTime < CACHE_EXPIRY_MS)) {
//     return developersDataCache;
//   }

//   // Fetch fresh data from the dashboard route which includes developer trends
//   const response = await fetch(`${API_BASE_URL}/development/dashboard`, {
//     headers: getAuthHeaders(),
//   });

//   if (!response.ok) throw new Error('API error');

//   const data = await response.json();
//   developersDataCache = data;
//   developersLastFetchTime = currentTime;

//   return data;
// }

// Iterations cache
let iterationsDataCache: any = null;
let iterationsLastFetchTime: number = 0;

// Function to get iteration data from the dedicated endpoint
export async function getIterationsData() {
  const currentTime = Date.now();

  // Use cached data if available and not expired
  if (iterationsDataCache && (currentTime - iterationsLastFetchTime < CACHE_EXPIRY_MS)) {
    return iterationsDataCache;
  }

  // Fetch fresh data
  const response = await fetch(`${API_BASE_URL}/development/iterations`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('API error');

  const data = await response.json();
  iterationsDataCache = data;
  iterationsLastFetchTime = currentTime;

  return data;
}



// Settings API functions
export async function getIntegrations() {
  // Add cache-busting query parameter to prevent caching
  const timestamp = new Date().getTime();
  const response = await fetch(`${API_BASE_URL}/settings/integrations?_=${timestamp}`, {
    headers: getAuthHeaders(),
    cache: 'no-store' // Tell browser not to use cache
  });
  if (!response.ok) throw new Error('API error');
  const data = await response.json()
  return data;
}

export async function getIntegrationConfig(integrationId: number) {
  // Add cache-busting query parameter to prevent caching
  const timestamp = new Date().getTime();
  const response = await fetch(`${API_BASE_URL}/settings/integrations/${integrationId}?_=${timestamp}`, {
    headers: getAuthHeaders(),
    cache: 'no-store' // Tell browser not to use cache
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function updateIntegrationStatus(integrationId: number, enabled: boolean) {
  const response = await fetch(`${API_BASE_URL}/settings/integrations/${integrationId}/status`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) throw new Error('API error');
  //clear cache
  invalidateAllCaches();
  return response.json();
}

export async function updateCustomerIntegrationStatus(customerIntegrationId: number, enabled: boolean) {
  const response = await fetch(`${API_BASE_URL}/settings/customer-integrations/${customerIntegrationId}/status`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) throw new Error('Failed to update customer integration status');
  //clear cache
  invalidateAllCaches();
  return response.json();
}

export async function refreshIntegrationStatus() {
  const response = await fetch(`${API_BASE_URL}/settings/integrations/refresh-status`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error('Failed to refresh integration status');
  //clear cache to ensure fresh data
  invalidateAllCaches();
  return response.json();
}

export function invalidateAllCaches() {
  invalidateDevelopmentDataCache();
  invalidateSupportDataCache();
}

export async function updateIntegrationConfig(
  integrationId: number,
  configValues: Record<string, any>,
  //schedule: any,
  customerIntegrationId?: number | null) {

  const dataToSend = {
    configValues: configValues,
    customerIntegrationId: customerIntegrationId ?? null,
    integrationId: integrationId
  };

  console.log('API: Sending integration config data:', dataToSend);

  const response = await fetch(`${API_BASE_URL}/settings/integrations/${integrationId}/config`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(dataToSend),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('API error updating integration config:', errorText);
    throw new Error(`API error: ${response.status} ${errorText}`);
  }

  return response.json();
}

export async function validateIntegrationConfig(integrationId: number, config: any) {
  const response = await fetch(`${API_BASE_URL}/settings/integrations/${integrationId}/validate`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
    },
    body: JSON.stringify(config),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getWorkItemTypes(
  integrationId: number,
  organizationUrl: string,
  projectName: string,
  apiToken: string,
) {
  let url: string;
  let queryParams: URLSearchParams;

  queryParams = new URLSearchParams({
    organizationUrl,
    projectName,
    apiToken
  });
  url = `${API_BASE_URL}/settings/integrations/${integrationId}/work-item-types?${queryParams}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getJiraFields(
  integrationId: number,
  jiraUrl: string,
  email: string,
  apiToken: string,
) {
  const queryParams = new URLSearchParams({
    jiraUrl,
    email,
    apiToken
  });
  const url = `${API_BASE_URL}/settings/integrations/${integrationId}/jira-fields?${queryParams}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getJiraProjects(
  integrationId: number,
  jiraUrl: string,
  email: string,
  apiToken: string,
) {
  const queryParams = new URLSearchParams({
    jiraUrl,
    email,
    apiToken
  });
  const url = `${API_BASE_URL}/settings/integrations/${integrationId}/jira-projects?${queryParams}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getCustomerIntegrationSecureConfigValues(integrationId: number, customerIntegrationId: number) {
  // Add cache-busting query parameter to prevent caching
  const timestamp = new Date().getTime();
  const response = await fetch(`${API_BASE_URL}/settings/integrations/${integrationId}/secure-config-values?customerIntegrationId=${customerIntegrationId}&_=${timestamp}`, {
    headers: getAuthHeaders(),
    cache: 'no-store' // Tell browser not to use cache
  });
  if (!response.ok) throw new Error('Failed to fetch integration metadata');
  return response.json();
}

export async function deleteCustomerIntegration(customerIntegrationId: number) {
  const response = await fetch(`${API_BASE_URL}/settings/integrations/customer/${customerIntegrationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to delete customer integration');
  return response.json();
}


export async function getFinance() {
  const response = await fetch(`${API_BASE_URL}/finance`);
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getTalent() {
  const response = await fetch(`${API_BASE_URL}/talent`);
  if (!response.ok) throw new Error('API error');
  return response.json();
}

export async function getSales() {
  const response = await fetch(`${API_BASE_URL}/sales`);
  if (!response.ok) throw new Error('API error');
  return response.json();
}

// Market Data API
export async function getMarketData(customerId: string | number) {
  const response = await fetch(`${API_BASE_URL}/marketdata/${customerId}`, {
    headers: getAuthHeaders(),
  });

  // If no data exists yet, return null instead of throwing an error
  if (response.status === 404) {
    return null;
  }

  if (!response.ok) throw new Error('Failed to fetch market data');
  return response.json();
}

export async function saveMarketData(marketData: any) {
  const response = await fetch(`${API_BASE_URL}/marketdata`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(marketData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to save market data');
  }

  return response.json();
}

export async function getDevelopmentDashboard() {
  const response = await fetch(`${API_BASE_URL}/development/dashboard`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch development dashboard data');
  return response.json();
}

export async function checkDevelopmentDataExists() {
  try {
    const response = await fetch(`${API_BASE_URL}/development/check-data-exists`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // If the endpoint returns 404, it means no data exists
      if (response.status === 404) {
        return { hasData: false };
      }
      throw new Error('Failed to check development data');
    }

    return response.json();
  } catch (error) {
    console.error('Error checking development data:', error);
    return { hasData: false };
  }
}

// Cache for support data
let supportDataCache: {
  data: SupportDashboardResponse | null;
  timestamp: number;
} = {
  data: null,
  timestamp: 0,
};

const SUPPORT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export async function getSupportData(): Promise<SupportDashboardResponse | null> {
  const now = Date.now();
  const isCacheValid = supportDataCache.data &&
    (now - supportDataCache.timestamp) < SUPPORT_CACHE_DURATION;

  if (isCacheValid) {
    return supportDataCache.data!;
  }

  const response = await fetch(`${API_BASE_URL}/support/dashboard`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    if (response.status === 404) {
      supportDataCache = {
        data: null,
        timestamp: now,
      };
      return null;
    }
    throw new Error('Failed to fetch support dashboard data');
  }

  const data = await response.json();
  if (data && data.summaryId) {
    supportDataCache = {
      data,
      timestamp: now,
    };
  }
  else {
    invalidateSupportDataCache();
  }


  return data;
}

export function invalidateSupportDataCache() {
  supportDataCache = {
    data: null,
    timestamp: 0,
  };
}

// Fetch staff member AI summary
export async function getSupportStaffAiSummary(staffName: string, summaryId: string | number): Promise<{ aiSummary: string, createdOn: string } | null> {
  const response = await fetch(`${API_BASE_URL}/support-ai/staff-ai-summary/${encodeURIComponent(staffName)}?summaryId=${summaryId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;//"No AI summary available for this staff member.";
    }
    throw new Error('Failed to fetch staff AI summary');
  }

  const data = await response.json();
  return data.aiSummary ? {
    aiSummary: data.aiSummary,
    createdOn: data.createdOn
  } : null;
}

export async function getSupportTeamAiSummary(summaryId: string | number): Promise<string | null> {
  const response = await fetch(`${API_BASE_URL}/support-ai/team-ai-summary/${summaryId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('Failed to fetch team AI summary');
  }

  const data = await response.json();
  return data.aiSummary || null;
}

// Fetch customer AI summary
export async function getSupportCustomerAiSummary(customerName: string, summaryId: string | number): Promise<{ summary: string; createdOn: string } | undefined> {
  const response = await fetch(`${API_BASE_URL}/support-ai/customer-ai-summary/${encodeURIComponent(customerName)}?summaryId=${summaryId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 404) {
      return undefined;
    }
    throw new Error('Failed to fetch customer AI summary');
  }

  const data = await response.json();
  return data.aiSummary ? {
    summary: data.aiSummary,
    createdOn: data.createdOn
  } : undefined;
}

export function invalidateDevelopmentDataCache() {
  developmentDataCache = null;
  lastFetchTime = 0;
}

export async function generateAiSummary(
  contextType: 'overview' | 'iteration' | 'team' |
    'developer' | 'project' | 'project-developer' |
    'developer-iteration' | 'developer-burndowns' |
    'all-iterations' | 'support-overview' | 'support-team' |
    'support-team-member' | 'support-customer' |
    'support-all-customers',
  entityId: string,
  projectId?: string,
  summaryId?: string,
  generateIfMissing?: boolean,
  forceRegenerate?: boolean) {
  const endpoint = {
    overview: '/development-ai/main-summary',
    iteration: `/development-ai/iteration-summary/${entityId}`,
    team: `/development-ai/team-summary/${entityId}`,
    developer: `/development-ai/developer-summary/${entityId}`,
    project: `/development-ai/project-summary/${entityId}`,
    'project-developer': `/development-ai/project-developer-summary/${entityId}`,
    'developer-iteration': `/development-ai/developer-iteration-summary/${entityId}`,
    'developer-burndowns': `/development-ai/developer-burndown-analysis/${entityId}`,
    'all-iterations': `/development-ai/all-iterations-summary/${entityId}`,
    'support-overview': `/support-ai/support-overview-summary/${entityId}`,
    'support-team': `/support-ai/support-team-summary/${entityId}`,
    'support-team-member': `/support-ai/support-teammember-summary/${entityId}`,
    'support-customer': `/support-ai/support-customer-summary/${entityId}`,
    'support-all-customers': `/support-ai/support-all-customers-summary/${entityId}`
  }[contextType];

  const url = new URL(`${API_BASE_URL}${endpoint}`);
  if (projectId) {
    url.searchParams.append('projectId', projectId);
  }
  if (summaryId) {
    url.searchParams.append('summaryId', summaryId);
  }
  if (generateIfMissing) {
    url.searchParams.append('generateIfMissing', generateIfMissing.toString());
  }
  else {
    url.searchParams.append('generateIfMissing', 'true');
  }

  if (forceRegenerate) {
    url.searchParams.append('forceRegenerate', forceRegenerate.toString());
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('Failed to generate AI summary');

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response stream available');

  //invalidate the correct cache based on the contextType
  switch (contextType) {
    case 'overview':
    case 'iteration':
    case 'team':
    case 'developer':
    case 'project':
    case 'project-developer':
    case 'developer-iteration':
    case 'developer-burndowns':
    case 'all-iterations':
      invalidateDevelopmentDataCache();
      break;
    case 'support-overview':
    case 'support-team':
    case 'support-team-member':
    case 'support-customer':
    case 'support-all-customers':
      invalidateSupportDataCache();
      break;
    default:
      break;
  }

  return {
    stream: reader,
    async *[Symbol.asyncIterator]() {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        yield new TextDecoder().decode(value);
      }
    }
  };
}
